import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SnackbarUtils from "../../libs/SnackbarUtils";
import { resetForm, updateForm } from "../../actions/ProgramForm.action";
import {
  serviceGetProgramDashboardDrop,
  serviceGetProgramDashboardDoctorWise,
  serviceGetProgramDashboardMedReminder,
  serviceGetProgramDashboardCity,
  serviceGetProgramDashboardCityWise,
  serviceGetProgramDashboardMedicineRefill,
  serviceGetProgramDashboardReward,
  serviceGetProgramDashboardEnrollment,
  serviceGetProgramDashboardMap,
  serviceProgramEnrollmentExport,
  serviceProgramTrendExport,
  serviceDoctorEnrollTrendExport,
} from "../../services/ProgramDashboard.service";
import history from "../../libs/history.utils";
import RouteName from "../../routes/Route.name";
import moment from "moment";

function useProgramDashboard() {
  const [rewardData, setRewardData] = useState({});
  const [cityData, setCityData] = useState([]);
  const [enrollmentData, setEnrollmentData] = useState([]);
  const [dropData, setDropData] = useState([]);
  const [cityWiseData, setCityWiseData] = useState([]);
  const [doctorWiseData, setDoctorWiseData] = useState([]);
  const [medicineRefill, setMedicineRefill] = useState([]);
  const [medReminder, setMedReminder] = useState([]);
  const [mapData, setMapData] = useState([]);
  const dispatch = useDispatch();
  const [fullDoctorWiseData, setFullDocWiseData] = useState([]);
  const [formValues, setFormValues] = useState({});

  const form = useSelector((state) => state.program_form);
  const [expandedView, setExpandedView] = useState({
    open: false,
    title: "",
    content: null,
  });
  const { user } = useSelector((state) => state.auth);

  const {
    user: { client_ids },
  } = useSelector((state) => state.auth);

  useEffect(() => {
    if (client_ids) {
      changeTextData(client_ids[0], "client_id");
    }
  }, [client_ids]);

  const changeTextData = useCallback(
    (text, fieldName) => {
      const updatedForm = { ...form };
      if (fieldName === "state_ids") {
        updatedForm["city_ids"] = [];
        updatedForm[fieldName] = text;
      } else if (fieldName === "client_id") {
        updatedForm["program_ids"] = [];
        updatedForm["city_ids"] = [];
        updatedForm["state_ids"] = [];
        updatedForm[fieldName] = text;
      } else if (fieldName === "program_ids") {
        updatedForm["city_ids"] = [];
        updatedForm["state_ids"] = [];
        updatedForm[fieldName] = text;
      } else if (fieldName === "start_date" || fieldName === "end_date") {
        const selectedDate = new Date(text);
        if (fieldName === "end_date") {
          const today = new Date();
          today.setHours(23, 59, 59, 999);
          if (selectedDate > today) {
            SnackbarUtils.error("End date should not be greater than today");
            updatedForm["end_date"] = "";
          } else {
            updatedForm[fieldName] = selectedDate;
          }
        } else {
          updatedForm[fieldName] = selectedDate;
        }
      } else {
        updatedForm[fieldName] = text;
      }
      if (updatedForm?.start_date && updatedForm?.end_date) {
        const startDate = new Date(updatedForm?.start_date);
        const endDate = new Date(updatedForm?.end_date);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        if (startDate.getTime() > endDate.getTime()) {
          SnackbarUtils.error("End Date cannot be less than Start Date");
          updatedForm["end_date"] = "";
        }
      }
      if (["client_id", "program_ids", "state_ids"]?.includes(fieldName)) {
        resetTableData();
      }
      dispatch(updateForm(updatedForm));
    },
    [form, dispatch]
  );

  const isFormComplete = useCallback(() => {
    return Object.entries(form).every(([key, value]) =>
      Array.isArray(value) ? value.length > 0 : Boolean(value)
    );
  }, [form]);

  const renderTotalReward = useCallback(
    (data) => {
      let req = serviceGetProgramDashboardReward({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const data = res?.data;
          setRewardData(data);
        }
      });
    },
    [setRewardData]
  );

  const renderMapData = useCallback(
    (data) => {
      let req = serviceGetProgramDashboardMap({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const data = res?.data;
          setMapData(data);
        }
      });
    },
    [setMapData]
  );

  const renderCityInfo = useCallback(
    (data) => {
      let req = serviceGetProgramDashboardCity({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const data = res?.data;
          setCityData(data);
        }
      });
    },
    [setCityData]
  );

  const renderEnrollmentInfo = useCallback(
    (data) => {
      let req = serviceGetProgramDashboardEnrollment({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const data = res?.data;
          setEnrollmentData(data);
        }
      });
    },
    [setEnrollmentData]
  );

  const renderDropInfo = useCallback(
    (data) => {
      let req = serviceGetProgramDashboardDrop({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const dataAvailable = res?.data
            // ?.filter((val) => val?.is_data_available)
            ?.map((val) => ({
              label: val?.label,
              dropout: val?.dropout,
              enrollment: val?.enrollment,
              // is_data_available: val?.is_data_available,
            }));
          setDropData(dataAvailable);
        }
      });
    },
    [setDropData]
  );

  const renderCityWiseInfo = useCallback(
    (data) => {
      let req = serviceGetProgramDashboardCityWise({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const data = res?.data;
          const result = data.map((item) => ({
            // id: item?.city?.id,
            name: item?.city?.name,
            enrollment: item?.enrollment,
            dropout: item?.dropout,
          }));
          setCityWiseData(result);
        }
      });
    },
    [setCityWiseData]
  );

  const renderDoctorWiseData = useCallback(
    (data) => {
      let req = serviceGetProgramDashboardDoctorWise({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const data = res?.data;
          setFullDocWiseData(data);
          const result = data?.map((item) => ({
            // id: item?.city?.id,
            name: item?.doctor?.name,
            enrollment: item?.enrollment,
            dropout: item?.dropout,
          }));
          setDoctorWiseData(result);
        }
      });
    },
    [setDoctorWiseData]
  );

  const renderMedicineRefill = useCallback(
    (data) => {
      let req = serviceGetProgramDashboardMedicineRefill({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const data = res?.data;
          const result = data?.map((val) => {
            return {
              label: val?.label,
              consumption_count: val?.consumption_count,
              refill_count: val?.refill_count,
            };
          });
          setMedicineRefill(result);
        }
      });
    },
    [setMedicineRefill]
  );

  const renderMedReminder = useCallback(
    (data) => {
      let req = serviceGetProgramDashboardMedReminder({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const data =
            res?.data?.map(({ label, MISSED, TAKEN, TAKEN_LATE, SKIPPED }) => ({
              label,
              MISSED,
              TAKEN,
              TAKEN_LATE,
              SKIPPED,
            })) || [];

          setMedReminder(data);
        }
      });
    },
    [setMedReminder]
  );
  useEffect(() => {
    if (isFormComplete()) {
      const updatedForm = {};

      for (const key in form) {
        const value = form[key];
        if (key === "program_ids") {
          updatedForm[key] = Array.isArray(value)
            ? value?.map((val) => val?.id)
            : [];
        } else if (["state_ids", "city_ids"].includes(key)) {
          const ids = Array.isArray(value) ? value?.map((val) => val?.id) : [];
          const hasAllFlag =
            ids?.includes("ALL_CITIES") || ids?.includes("PAN_INDIA");
          updatedForm[key] = hasAllFlag ? [] : ids;
        } else if (["start_date", "end_date"]?.includes(key)) {
          updatedForm[key] = value ? moment(value)?.format("YYYY-MM-DD") : "";
        } else {
          updatedForm[key] = value ?? "";
        }
      }
      setFormValues(updatedForm);
      renderTotalReward(updatedForm);
      renderCityInfo(updatedForm);
      renderMapData(updatedForm);
      renderEnrollmentInfo(updatedForm);
      renderDropInfo(updatedForm);
      renderCityWiseInfo(updatedForm);
      renderDoctorWiseData(updatedForm);
      renderMedicineRefill(updatedForm);
      renderMedReminder(updatedForm);
    }
  }, [form]);

  const handleDetail = useCallback((data) => {
    history.push(RouteName.PROGRAM_DETAIL + data?.id);
  }, []);

  const groupedData = useMemo(() => {
    return dropData?.reduce((acc, item) => {
      Object?.keys(item)?.forEach((key) => {
        if (!acc[key]) acc[key] = [];
        acc[key]?.push(item[key]);
      });
      return acc;
    }, {});
  }, [dropData]);

  const resetTableData = useCallback(() => {
    setRewardData({});
    setCityData([]);
    setEnrollmentData([]);
    setDropData([]);
    setCityWiseData([]);
    setDoctorWiseData([]);
    setMedicineRefill([]);
    setMedReminder([]);
    setMapData([]);
  }, []);
  const handleReset = useCallback(() => {
    dispatch(resetForm());
    resetTableData();
  }, []);

  const handleExpand = useCallback((title, component) => {
    setExpandedView({
      open: true,
      title,
      content: component,
    });
  }, []);

  const downloadBlob = useCallback((blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, []);

  const handleProgramEnrollExport = useCallback(() => {
    try {
      serviceProgramEnrollmentExport({ ...formValues }, user?.token).then(
        async (res) => {
          const blob = await res.blob();
          downloadBlob(blob, "program_performance_report.csv");
        }
      );
    } catch (e) {
      console.log("error", e);
    }
  }, [formValues]);

  const handleProgramTrendExport = useCallback(() => {
    try {
      serviceProgramTrendExport({ ...formValues }, user?.token).then(
        async (res) => {
          const blob = await res.blob();
          downloadBlob(blob, "program_performance_report.csv");
        }
      );
    } catch (e) {
      console.log("error", e);
    }
  }, [formValues]);

  const handleDoctorWiseData = useCallback(() => {
    try {
      if (fullDoctorWiseData?.length > 0) {
        const docIds = fullDoctorWiseData?.map((val) => val.doctor.id);
        serviceDoctorEnrollTrendExport(
          { ...formValues, doctor_ids: docIds },
          user?.token
        ).then(async (res) => {
          const blob = await res.blob();
          downloadBlob(blob, "doctor_enroll_trend_report.csv");
        });
      }
    } catch (e) {
      console.log("error ???? ", e);
    }
  }, [formValues, fullDoctorWiseData]);

  return {
    form,
    changeTextData,
    rewardData,
    cityData,
    dropData,
    cityWiseData,
    doctorWiseData,
    handleDetail,
    medicineRefill,
    medReminder,
    enrollmentData,
    mapData,
    groupedData,
    handleReset,
    expandedView,
    handleExpand,
    setExpandedView,
    handleProgramEnrollExport,
    handleProgramTrendExport,
    handleDoctorWiseData,
  };
}

export default useProgramDashboard;
